import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { taskKeys } from '@/features/tasks/useTasks';

export interface TimerState {
  running: boolean;
  task_id: number | null;
  task_title: string | null;
  elapsed_seconds: number;
  runaway: boolean;
  entry: { id: number; started_at: string } | null;
}

const TIMER_KEY = ['timer'] as const;

export function useTimer() {
  return useQuery<TimerState>({
    queryKey: TIMER_KEY,
    queryFn: () => api<TimerState>('/api/timer'),
    // The server is the authority on what is running. Re-checking on focus
    // catches a timer started on another device, or stopped while this tab
    // was asleep.
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

/**
 * Seconds elapsed, counted locally from the server's `started_at`.
 *
 * Never from an incrementing counter: a backgrounded tab throttles timers, so
 * a counter drifts while a difference of timestamps cannot.
 */
export function useElapsed(startedAt: string | null | undefined): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    const onVisible = () => setNow(Date.now());
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [startedAt]);

  if (!startedAt) return 0;
  return Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
}

export function useTimerControls() {
  const qc = useQueryClient();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: TIMER_KEY });
    qc.invalidateQueries({ queryKey: taskKeys.all });
  };

  const start = useMutation<TimerState, ApiError, number>({
    mutationFn: (taskId) =>
      api<TimerState>('/api/timer/start', { method: 'POST', json: { task_id: taskId } }),
    onSuccess: invalidate,
    onError: (error) => toast.error(error.message),
  });

  const stop = useMutation<TimerState, ApiError, void>({
    mutationFn: () => api<TimerState>('/api/timer/stop', { method: 'POST' }),
    onSuccess: invalidate,
    onError: (error) => toast.error(error.message),
  });

  return { start, stop };
}

export function useLogTime() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { task_id: number; minutes: number; note?: string }>({
    mutationFn: (input) => api('/api/time-entries', { method: 'POST', json: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all }),
    onError: (error) => toast.error(error.message),
  });
}

/** `5413` -> `1:30:13`. Always h:mm:ss above an hour, m:ss below. */
export function formatClock(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
}
